// ============================================================
// Header — Titel, Vergleichs-Toggle, Perioden-Picker
// ============================================================

import { useEffect, useRef, useState } from 'react';
import { Icon } from '../icons';
import { readableOnBrand, CO } from '../theme';

import { useApp, useEntries } from '../store';
import { dataBounds, period } from '../aggregator';
import { dstr, fmtNum } from '../format';
import type { PeriodMode, RouteKey } from '../types';

const ROUTE_META: Record<RouteKey, { l: string; d: string }> = {
  uebersicht: { l: 'Übersicht', d: 'Finanzielle Transparenz auf einen Blick' },
  zeitverlauf: { l: 'Zeitverlauf', d: 'Einsatz und Auszahlung über die Zeit' },
  spielarten: { l: 'Spielarten', d: 'Neutraler Vergleich der Spielarten' },
  muster: { l: 'Muster & Serien', d: 'Historische Muster im importierten Datensatz' },
  einzelbuchungen: { l: 'Einzelbuchungen', d: 'Alle gebuchten Einträge im Detail' },
  datenimport: { l: 'Datenimport', d: 'Datensatz prüfen oder ersetzen' },
};

const PERIOD_OPTIONS: { k: PeriodMode; l: string; group: 'cal' | 'rolling' | 'custom' }[] = [
  { k: 'gesamt', l: 'Gesamtzeitraum', group: 'cal' },
  { k: 'curMonth', l: 'Aktueller Kalendermonat', group: 'cal' },
  { k: 'lastMonth', l: 'Letzter Kalendermonat', group: 'cal' },
  { k: 'curYear', l: 'Aktuelles Kalenderjahr', group: 'cal' },
  { k: 'lastYear', l: 'Letztes Kalenderjahr', group: 'cal' },
  { k: 'd30', l: 'Letzte 30 Tage', group: 'rolling' },
  { k: 'd90', l: 'Letzte 90 Tage', group: 'rolling' },
  { k: 'd365', l: 'Letzte 365 Tage', group: 'rolling' },
  { k: 'custom', l: 'Benutzerdefinierter Zeitraum', group: 'custom' },
];

export function Header({ filteredCount }: { filteredCount: number }) {
  const { route, periodMode, setPeriodMode, customFrom, customTo, setCustomFrom, setCustomTo, compare, setCompare, history } = useApp();
  const entries = useEntries();

  const meta = ROUTE_META[route];
  const p = period(periodMode, entries, customFrom, customTo);
  const bounds = dataBounds(entries);
  const brand = CO.sidebar;
  const T = readableOnBrand(brand);

  // Berechnen wir Anzeige-Daten
  const expDate = history?.exportiertAm ? new Date(history.exportiertAm) : null;
  const expLabel = expDate
    ? `${String(expDate.getDate()).padStart(2, '0')}.${String(expDate.getMonth() + 1).padStart(2, '0')}.${expDate.getFullYear()}, ${String(expDate.getHours()).padStart(2, '0')}:${String(expDate.getMinutes()).padStart(2, '0')}`
    : '–';
  const hasData = entries.length > 0;

  return (
    <header
      style={{
        background: CO.card,
        borderBottom: `1px solid ${CO.border}`,
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 20,
        flexWrap: 'wrap',
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      <div>
        <h1 style={{ margin: 0, fontSize: 21, fontWeight: 700, letterSpacing: '-.3px' }}>{meta.l}</h1>
        <div style={{ fontSize: 13, color: CO.muted, marginTop: 2 }}>{meta.d}</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'right', lineHeight: 1.3 }}>
          <div style={{ fontSize: 11.5, color: CO.faint }}>Letzter Import</div>
          <div className="num" style={{ fontSize: 12.5, color: CO.muted, fontWeight: 500 }}>{expLabel}</div>
          <div style={{ fontSize: 11.5, color: CO.muted, marginTop: 2 }}>
            {hasData ? `${fmtNum(filteredCount)} Einträge im Zeitraum` : 'Keine Daten'}
          </div>
        </div>

        <div style={{ width: 1, height: 34, background: CO.border }} />

        <CompareToggle />

        <PeriodPicker />
      </div>
    </header>
  );
}

function CompareToggle() {
  const { compare, setCompare } = useApp();
  const brand = CO.sidebar;
  return (
    <button
      onClick={() => setCompare(!compare)}
      aria-pressed={compare}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        background: 'transparent',
        border: `1px solid ${compare ? brand : CO.border}`,
        borderRadius: 9,
        padding: '7px 11px',
        color: compare ? brand : CO.muted,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          width: 34,
          height: 18,
          borderRadius: 20,
          background: compare ? brand : '#cfd4ca',
          position: 'relative',
          transition: 'background .2s',
          flex: '0 0 34px',
          display: 'inline-block',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: compare ? 18 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left .2s',
          }}
        />
      </span>
      <span style={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>Mit vorherigem Zeitraum vergleichen</span>
    </button>
  );
}

function PeriodPicker() {
  const { periodMode, setPeriodMode, customFrom, customTo, setCustomFrom, setCustomTo } = useApp();
  const entries = useEntries();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const p = period(periodMode, entries, customFrom, customTo);
  const brand = CO.sidebar;
  const T = readableOnBrand(brand);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const rangeStr = `${dstr(p.from.toISOString())} – ${dstr(p.to.toISOString())}`;

  const grouped = {
    cal: PERIOD_OPTIONS.filter((o) => o.group === 'cal'),
    rolling: PERIOD_OPTIONS.filter((o) => o.group === 'rolling'),
    custom: PERIOD_OPTIONS.filter((o) => o.group === 'custom'),
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: brand,
          border: 'none',
          borderRadius: 9,
          padding: '9px 13px',
          color: T.fg,
          minWidth: 240,
          cursor: 'pointer',
        }}
      >
        <Icon name="cal" color={T.fg} size={16} />
        <div style={{ textAlign: 'left', flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{p.label}</div>
          <div className="num" style={{ fontSize: 11, color: T.fg80 }}>{rangeStr}</div>
        </div>
        <span style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s', display: 'inline-flex' }}>
          <Icon name="chev" color={T.fg} size={15} />
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            background: '#fff',
            border: `1px solid ${CO.border}`,
            borderRadius: 12,
            boxShadow: '0 14px 40px rgba(0,0,0,.16)',
            padding: 8,
            width: 320,
            zIndex: 40,
            animation: 'wlPop .16s ease',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: CO.faint,
              textTransform: 'uppercase',
              letterSpacing: '.5px',
              padding: '6px 10px 4px',
            }}
          >
            Kalenderzeiträume
          </div>
          {grouped.cal.map((o) => (
            <PeriodOpt key={o.k} k={o.k} l={o.l} />
          ))}
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: CO.faint,
              textTransform: 'uppercase',
              letterSpacing: '.5px',
              padding: '8px 10px 4px',
            }}
          >
            Rollierende Zeiträume
          </div>
          {grouped.rolling.map((o) => (
            <PeriodOpt key={o.k} k={o.k} l={o.l} />
          ))}
          <div style={{ borderTop: `1px solid ${CO.border2}`, marginTop: 6, paddingTop: 6 }}>
            <PeriodOpt k={grouped.custom[0].k} l={grouped.custom[0].l} />
          </div>

          {periodMode === 'custom' ? (
            <div style={{ padding: 10, borderTop: `1px solid ${CO.border2}`, marginTop: 6 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <label style={{ flex: 1, fontSize: 11.5, color: CO.muted }}>
                  Von
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 3,
                      padding: '6px 8px',
                      border: `1px solid ${CO.border}`,
                      borderRadius: 7,
                      fontSize: 12.5,
                      fontFamily: 'inherit',
                    }}
                  />
                </label>
                <label style={{ flex: 1, fontSize: 11.5, color: CO.muted }}>
                  Bis
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 3,
                      padding: '6px 8px',
                      border: `1px solid ${CO.border}`,
                      borderRadius: 7,
                      fontSize: 12.5,
                      fontFamily: 'inherit',
                    }}
                  />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    flex: 1,
                    padding: '7px',
                    border: 'none',
                    borderRadius: 7,
                    background: brand,
                    color: T.fg,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Übernehmen
                </button>
                <button
                  onClick={() => {
                    setCustomFrom('');
                    setCustomTo('');
                  }}
                  style={{
                    flex: 1,
                    padding: '7px',
                    border: `1px solid ${CO.border}`,
                    borderRadius: 7,
                    background: '#fff',
                    color: CO.muted,
                    fontSize: 12.5,
                    cursor: 'pointer',
                  }}
                >
                  Zurücksetzen
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PeriodOpt({ k, l }: { k: PeriodMode; l: string }) {
  const { periodMode, setPeriodMode } = useApp();
  const active = periodMode === k;
  const brand = CO.sidebar;
  return (
    <button
      role="option"
      aria-selected={active}
      onClick={() => {
        setPeriodMode(k);
        if (k !== 'custom') setPeriodMode(k);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        textAlign: 'left',
        border: 'none',
        background: active ? '#eef5ef' : 'transparent',
        color: active ? brand : CO.text,
        padding: '8px 10px',
        borderRadius: 7,
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      <span>{l}</span>
      {active ? <Icon name="check" color={brand} size={15} /> : null}
    </button>
  );
}
